/**
 * « Mon travail » : les cinq actions du chapitre de chaque profil dans le
 * manuel 2 « L'espace de votre paroisse » (décision PO du 10/10/2026).
 *
 * Le manuel est rangé par profil, cinq actions numérotées par chapitre. En
 * mode travail, ces cinq actions montent en haut du menu et de l'accueil ;
 * les quatorze sections de la page 6 restent dessous, sous « Tout l'espace ».
 * Les titres reprennent ceux du manuel, avec leur numéro : la personne qui a
 * le manuel en main retrouve la même chose à l'écran.
 *
 * Chaque geste ouvre l'écran (id de page de `nav-map`) où il se fait ; il
 * n'apparaît que si le rôle a le droit d'ouvrir cet écran.
 *
 * i18n-dynamic: common.monTravail.*
 */
import { canAccessPage } from "@/lib/role-permissions";
import { getPageRoute } from "@/lib/nav-map";

export type Geste = {
  /**
   * Numéro de l'action dans le manuel 2 (« Action 12 »). Pour un geste de
   * l'encart du 20/10, « E » suivi de l'écran (« E81 ») : identifiant
   * seulement, l'écran affiche « Encart du 20/10 ».
   */
  num: string;
  /** Clé i18n sous `common.monTravail.gestes.<cle>` (titre et description). */
  cle: string;
  /** Écran où le geste se fait (id de page). */
  pageId: number;
  /**
   * Geste ajouté par l'encart du 20/10 « L'argent de la paroisse » (audit de
   * la boucle financière, BF15) : il n'a pas de numéro dans le manuel.
   */
  encart?: true;
  /** Lien précis du geste, quand l'écran s'ouvre sur une vue donnée. */
  route?: string;
  /** Écran nouveau, encore en essai (« Ma semaine », 02/11/2026) : hors numérotation du manuel. */
  nouveau?: true;
};

export type Chapitre = {
  /** Clé i18n du titre de chapitre (`common.monTravail.chapitres.<role>`). */
  role: string;
  gestes: Geste[];
};

const G = (num: string, cle: string, pageId: number): Geste => ({ num, cle, pageId });

/** Geste de l'encart du 20/10, hors numérotation du manuel. */
const E = (cle: string, pageId: number, route?: string): Geste => ({
  num: `E${pageId}`, cle, pageId, encart: true, ...(route ? { route } : {}),
});

/** Geste d'un écran nouveau, hors numérotation du manuel. */
const N = (cle: string, pageId: number): Geste => ({ num: `N${pageId}`, cle, pageId, nouveau: true });

/**
 * Boucle de l'argent (audit du 20/10/2026, segments 7 et 8) : le curé
 * garde ses cinq actions du manuel et gagne « Dons en ligne » (81),
 * « Valider la semaine » (9) et « Mes reversements » (90) ; rien n'est
 * retiré. La secrétaire trouve « Encaissements au guichet » (91) en tête.
 */
export const CHAPITRES: Record<string, Geste[]> = {
  cure: [
    G("07", "alertes", 2), G("08", "ficheFidele", 3), G("09", "registres", 6),
    G("10", "agenda", 66), G("11", "convoquerConseil", 62),
    // « Valider la semaine écoulée » ouvre la clôture sur la semaine écoulée,
    // et son libellé le dit (décision du PO du 22/10/2026).
    // « Ma semaine » (demande du PO du 02/11/2026) : saisir, corriger, valider
    // et verser au même endroit ; les gestes d'avant restent.
    E("donsEnLigne", 81), N("maSemaine", 93), E("validerSemaine", 9, "/cloture?semaine=ecoulee"), E("mesReversements", 90),
  ],
  vicaire: [
    G("12", "planningLiturgique", 79), G("13", "intentionsMesse", 7), G("14", "preparationMariage", 80),
    G("15", "visitesMalades", 83), G("16", "groupesPriere", 82),
  ],
  econome: [
    N("maSemaine", 93),
    G("17", "nouvelleEcriture", 8), G("18", "donsDesMesses", 77), G("19", "clotureSemaine", 9),
    G("20", "mobileMoney", 81), G("21", "cockpit", 8),
  ],
  secretaire_general: [
    G("22", "envoyerMessage", 10), G("23", "siteWeb", 11), G("24", "courrier", 67),
    G("25", "archives", 36), G("26", "convoquerConseilSg", 62),
  ],
  secretaire: [
    E("encaissementsGuichet", 91),
    G("27", "enrolerParoissien", 3), G("28", "nouvelleIntention", 7), G("29", "ajouterCalendrier", 24),
    G("30", "reserverSalle", 26), G("31", "funerailles", 30),
  ],
  responsable_mouvement: [
    G("32", "demandesAdhesion", 45), G("33", "kermesse", 85), G("34", "nouvelEvenement", 25),
    G("35", "catechese", 41), G("36", "evenementCalendrier", 24),
  ],
  conseiller: [
    G("37", "conseilPastoral", 62), G("38", "analyticsPastoral", 52), G("39", "retoursFideles", 59),
    G("40", "planAmelioration", 59), G("41", "tableauCure", 2),
  ],
  benevole: [
    G("42", "portailParoissien", 87), G("43", "mesTaches", 45), G("44", "calendrier", 24),
    G("45", "rejoindreGroupe", 45), G("46", "aidesSociales", 84),
  ],
};

/** Lien d'un geste : son lien précis s'il en a un, sinon la route de son écran. */
export function routeDuGeste(g: Pick<Geste, "pageId" | "route">): string {
  return g.route ?? getPageRoute(g.pageId);
}

/**
 * Gestes du chapitre du rôle, limités aux écrans qu'il peut ouvrir et allumés
 * dans cet environnement (`masquees`). Vide : pas de chapitre.
 */
export function gestesDuRole(role: string, masquees: ReadonlySet<number> = new Set()): Geste[] {
  return (CHAPITRES[role] ?? []).filter((g) => canAccessPage(role, g.pageId) && !masquees.has(g.pageId));
}

/** Le rôle a-t-il un chapitre dans le manuel (donc un mode travail) ? */
export function aUnChapitre(role: string): boolean {
  return gestesDuRole(role).length > 0;
}

/**
 * Étiquette d'un geste : « Action 12 » pour une action du manuel, « Encart
 * du 20/10 » pour un geste ajouté par l'encart (pas de numéro inventé).
 */
export function etiquetteGeste(g: Geste, t: (cle: string, params?: Record<string, string | number>) => string): string {
  if (g.nouveau) return t("common.monTravail.nouveau");
  return g.encart ? t("common.monTravail.encart") : t("common.monTravail.action", { num: g.num });
}

/**
 * « Au quotidien » : actions 02 à 05 du chapitre commun « Se connecter et se
 * repérer » (l'assistance, Action 06, reste en bas du menu ; les
 * notifications, Action 03, gardent leur entrée et leur pastille).
 */
export const QUOTIDIEN: Geste[] = [
  G("02", "retrouverParoissien", 3),
  G("04", "ficheParoisse", 76),
  // Les horaires de messe font partie de l'Action 04 du manuel (« corriger
  // les horaires... affichés aux fidèles ») ; depuis le 27/10/2026 ils ont
  // leur propre écran, ouvert au curé, au vicaire et à la secrétaire.
  G("04", "horairesMesses", 92),
  G("05", "portailParoissien", 87),
];

export function quotidienDuRole(role: string): Geste[] {
  const dejaLa = new Set(gestesDuRole(role).map((g) => g.pageId));
  return QUOTIDIEN.filter((g) => canAccessPage(role, g.pageId) && !dejaLa.has(g.pageId));
}
